<template>
  <div>
    <b-overlay :show="isLoading" rounded="sm">
      <b-container fluid>
        <b-navbar toggleable="lg" type="dark" variant="light">
          <b-navbar-brand href="#" class="text-dark">
            <strong>Due Diligence Categories</strong>
          </b-navbar-brand>

          <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>

          <b-collapse id="nav-collapse" is-nav>
            <!-- Right aligned nav items -->
            <b-navbar-nav class="ml-auto">
              <b-nav-form>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="showModal = !showModal"
                  class="mx-2"
                >
                  <font-awesome-icon icon="plus" />
                </b-button>

                <b-button class="mr-2" variant="outline-info" size="sm" @click="reloadTable">
                  <font-awesome-icon icon="sync" />
                </b-button>
                <b-form-group>
                  <b-input-group size="sm">
                    <b-form-input
                      v-model="filter"
                      type="search"
                      id="filterInput"
                      placeholder="Type to Search"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-nav-form>
            </b-navbar-nav>
          </b-collapse>
        </b-navbar>

        <b-table
          class="mx-1"
          bordered
          sticky-header="calc(100vh - 200px)"
          ref="table"
          id="my-table"
          :busy.sync="isLoading"
          :items="getDueDiligenceCategoriesTableData"
          :fields="tableFields"
          :filter="filter"
          :per-page="perPage"
          :current-page="currentPage"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          responsive="sm"
        >
          <template v-slot:cell(actions)="data">
            <div class="text-nowrap">
              <b-button
                size="sm"
                class="mr-2"
                type="button"
                variant="outline-info"
                @click="onEditDueDiligenceCategories(data)"
              >
                <font-awesome-icon v-b-tooltip.hover title="Edit Field" icon="edit" />
              </b-button>
              <b-button
                size="sm"
                type="button"
                variant="outline-danger"
                @click="onDeleteDueDiligenceCategories(data)"
              >
                <font-awesome-icon v-b-tooltip.hover title="Delete Field" icon="trash" />
              </b-button>
            </div>
          </template>
        </b-table>
        <b-row>
          <b-col cols="2">
            <b-form-select v-model="perPage" id="perPageSelect" size="sm" :options="pageOptions"></b-form-select>
          </b-col>
          <b-col>
            <b-pagination
              class="float-right"
              v-model="currentPage"
              :total-rows="rows"
              :per-page="perPage"
              first-text="First"
              prev-text="Prev"
              next-text="Next"
              last-text="Last"
              aria-controls="my-table"
            ></b-pagination>
          </b-col>
        </b-row>
      </b-container>
    </b-overlay>
    <b-modal
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      size="lg"
      :title="editDueDiligenceCategories ? 'Update Due Diligence Categories' : 'Create Due Diligence Categories'"
      v-model="showModal"
    >
      <div>
        <div v-if="!submiting && !responseSuccess">
          <b-row>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Title :
                  <span class="required-span">*</span>
                </label>
                <b-form-input
                  v-model="$v.form.titleInput.$model"
                  :state="$v.form.titleInput.$dirty ? !$v.form.titleInput.$error : null"
                  placeholder="Enter Title"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Abbreviation :
                  <span class="required-span">*</span>
                </label>
                <b-form-input
                  v-model="$v.form.abbreviationInput.$model"
                  :state="$v.form.abbreviationInput.$dirty ? !$v.form.abbreviationInput.$error : null"
                  placeholder="Enter Abbreviation"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12">
              <b-form-group label="Description:">
                <b-form-textarea
                  v-model="form.descriptionInput"
                  rows="2"
                  max-rows="2"
                  placeholder="Enter Description"
                ></b-form-textarea>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
        <div v-else-if="submiting && !responseSuccess">
          <p>{{editDueDiligenceCategories ? 'Your Due Diligence Categories is being Updating......' : 'Your Due Diligence Categories is being Creating......'}}</p>
          <b-spinner type="grow" label="Loading..."></b-spinner>
        </div>
        <div v-else>
          <p class="my-2 text-center">
            {{ editDueDiligenceCategories ? 'Your Due Diligence Categories has been updated successfully.' : 'Your Due Diligence Categories has been added successfully.' }}
            <br />
          </p>
        </div>
      </div>
      <template v-slot:modal-footer>
        <b-button v-if="!submiting" variant="outline-danger" @click="resetModal">Close</b-button>
        <b-button
          v-if="!submiting && !responseSuccess"
          variant="outline-success"
          @click="checkValidation()"
        >{{ editDueDiligenceCategories? 'Update' : 'Save' }}</b-button>
      </template>
    </b-modal>
    <b-overlay :show="showDeleteDueDiligenceCategories" no-wrap>
      <template v-slot:overlay>
        <div v-if="processing" class="text-center p-4 bg-primary text-light rounded">
          <font-awesome-icon size="lg" icon="cloud-upload-alt" />
          <div class="mb-3">Deleting...</div>
          <b-progress
            min="1"
            max="20"
            :value="counter"
            variant="success"
            height="3px"
            class="mx-n4 rounded-0"
          ></b-progress>
        </div>
        <div
          v-else
          ref="dialog"
          tabindex="-1"
          role="dialog"
          aria-modal="false"
          aria-labelledby="form-confirm-label"
          class="text-center p-3"
        >
          <p>
            <strong id="form-confirm-label">Are you sure?</strong>
          </p>
          <div class="d-flex">
            <b-button variant="outline-success" class="mr-3" @click="resetDeleteModal">Cancel</b-button>
            <b-button variant="outline-danger" @click="deleteDueDiligenceCategoriesCall">Yes</b-button>
          </div>
        </div>
      </template>
    </b-overlay>
  </div>
</template>
<script>
import { HTTP } from "../../utils/HTTP";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
export default {
  name: "PriSettingsDueDiligenceCategories",
  mixins: [validationMixin],
  data() {
    return {
      form: {
        titleInput: null,
        abbreviationInput: null,
        descriptionInput: null
      },
      showDeleteDueDiligenceCategories: false,
      isLoading: false,
      submiting: false,
      responseSuccess: false,
      showModal: false,
      editDueDiligenceCategories: false,
      tableFields: [
        { key: "created_at", label: "Age", sortable: true },
        { key: "title", label: "Title", sortable: true },
        { key: "abbreviation", label: "Abbreviation", sortable: true },
        { key: "description", label: "Description", sortable: true },
        { key: "actions", label: "Actions", sortable: false }
      ],
      filter: null,
      perPage: 10,
      totalRows: 0,
      currentPage: 1,
      sortBy: "id",
      sortDesc: true,
      pageOptions: [5, 10, 25, 50, 75, 100],
      dueDiligenceCategoriesId: null,
      processing: false,
      counter: 1,
      interval: null
    };
  },
  validations: {
    form: {
      titleInput: { required },
      abbreviationInput: { required }
    }
  },
  mounted() {},
  computed: {
    rows() {
      return this.totalRows;
    }
  },
  beforeDestroy() {
    this.clearInterval();
  },
  methods: {
    clearInterval() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    },
    reloadTable() {
      this.$root.$emit("bv::refresh::table", "my-table");
    },
    async getDueDiligenceCategoriesTableData() {
      let formData = {
        filter: this.filter,
        sort: this.sortBy + "|" + (this.sortDesc == true ? "desc" : "asc"),
        per_page: this.perPage,
        page: this.currentPage
      };

      let token = "Bearer " + localStorage.getItem("accessToken");
      this.isLoading = true;
      let result = HTTP("post", "v1/get-due-diligence-categories", formData, {
        Authorization: token
      });

      return result
        .then(response => {
          const items = response.data.data;
          this.isLoading = false;
          this.totalRows = response.data.total;
          return items;
        })
        .catch(error => {
          this.isLoading = false;
          return [error];
        });
    },
    resetForm() {
      this.form = {
        titleInput: null,
        descriptionInput: null
      };
    },
    resetModal() {
      this.resetForm();
      this.isLoading = false;
      this.submiting = false;
      this.responseSuccess = false;
      this.showModal = false;
      this.editDueDiligenceCategories = false;
      this.dueDiligenceCategoriesId = null;
      this.$v.$reset();
    },
    checkValidation() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      } else if (this.editDueDiligenceCategories) {
        this.updateDueDiligenceCategories().then(this.reloadTable);
      } else {
        this.saveDueDiligenceCategories().then(this.reloadTable);
      }
    },
    async saveDueDiligenceCategories() {
      let formData = {
        title: this.form.titleInput,
        abbreviation: this.form.abbreviationInput,
        description: this.form.descriptionInput
      };
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        this.submiting = true;
        this.isLoading = true;
        await HTTP("post", "v1/create-due-diligence-categories", formData, {
          Authorization: token
        });
        this.submiting = false;
        this.isLoading = false;
        this.responseSuccess = true;
      } catch (err) {
        return [err];
        // // console.log("------------erroror", err);
      }
    },
    onEditDueDiligenceCategories(data) {
      this.editDueDiligenceCategories = true;
      this.dueDiligenceCategoriesId = data.item.id;
      this.form.titleInput = data.item.title;
      this.form.abbreviationInput = data.item.abbreviation;
      this.form.descriptionInput = data.item.description;
      this.showModal = true;
    },
    async updateDueDiligenceCategories() {
      let formData = {
        due_diligence_categories_id: this.dueDiligenceCategoriesId,
        title: this.form.titleInput,
        abbreviation: this.form.abbreviationInput,
        description: this.form.descriptionInput
      };
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        this.submiting = true;
        this.isLoading = true;
        await HTTP("post", "v1/update-due-diligence-categories", formData, {
          Authorization: token
        });
        this.submiting = false;
        this.isLoading = false;
        this.responseSuccess = true;
      } catch (err) {
        return [err];
        // // console.log("------------erroror", err);
      }
    },
    onDeleteDueDiligenceCategories(data) {
      this.dueDiligenceCategoriesId = data.item.id;
      this.processing = false;
      this.showDeleteDueDiligenceCategories = true;
    },
    deleteDueDiligenceCategoriesCall() {
      this.counter = 1;
      this.processing = true;
      this.deleteDueDiligenceCategories().then(this.reloadTable);
      this.clearInterval();
      this.interval = setInterval(() => {
        if (this.counter < 20) {
          this.counter = this.counter + 1;
        } else {
          this.clearInterval();
          this.$nextTick(() => {
            this.showDeleteDueDiligenceCategories = this.processing = false;
          });
        }
      }, 350);
    },
    async deleteDueDiligenceCategories() {
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        await HTTP(
          "get",
          "v1/delete-due-diligence-categories/" + this.dueDiligenceCategoriesId,
          null,
          {
            Authorization: token
          }
        );
        this.dueDiligenceCategoriesId = null;
      } catch (error) {
        this.isLoading = false;
      }
    },
    resetDeleteModal() {
      this.showDeleteDueDiligenceCategories = false;
    }
  }
};
</script>